import { useCallback, useEffect, useState } from "react";
import { hasSupabaseConfig, supabase } from "../lib/supabaseClient";

// "lifetime" = the one-time Pro purchase. "active"/"trialing" stay for any legacy subscription.
const ACTIVE_SUBSCRIPTION_STATES = new Set(["lifetime", "active", "trialing"]);
const PERMANENT_PRO_EMAILS = new Set(["rene.reede@gmail.com"]);

function isPermanentProUser(user) {
  const email = typeof user?.email === "string" ? user.email.trim().toLowerCase() : "";
  return PERMANENT_PRO_EMAILS.has(email);
}

function getIsPro(subscription) {
  if (!ACTIVE_SUBSCRIPTION_STATES.has(subscription?.subscription_status)) {
    return false;
  }
  // Belt-and-suspenders: if a webhook was ever missed, don't keep granting Pro
  // past the paid period. A missing period end means "trust the status".
  if (subscription.current_period_end) {
    const end = new Date(subscription.current_period_end).getTime();
    if (Number.isFinite(end) && end < Date.now()) {
      return false;
    }
  }
  return true;
}

export function useAccountStatus() {
  const [state, setState] = useState({
    configured: hasSupabaseConfig,
    loading: true,
    session: null,
    user: null,
    subscription: null,
    isPro: false,
    error: "",
  });

  const refresh = useCallback(async () => {
    if (!supabase) {
      setState((current) => ({
        ...current,
        configured: false,
        loading: false,
        session: null,
        user: null,
        subscription: null,
        isPro: false,
      }));
      return;
    }

    setState((current) => ({ ...current, loading: true, error: "" }));

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      setState((current) => ({
        ...current,
        loading: false,
        session: null,
        user: null,
        subscription: null,
        isPro: false,
        error: sessionError.message,
      }));
      return;
    }

    const session = sessionData.session;
    if (!session?.user) {
      setState((current) => ({
        ...current,
        loading: false,
        session: null,
        user: null,
        subscription: null,
        isPro: false,
      }));
      return;
    }

    // Rene's authenticated owner account has permanent access independent of
    // Stripe. Supabase supplies the signed-in email; local storage cannot grant
    // this entitlement.
    if (isPermanentProUser(session.user)) {
      setState((current) => ({
        ...current,
        loading: false,
        session,
        user: session.user,
        subscription: null,
        isPro: true,
        error: "",
      }));
      return;
    }

    const { data: subscription, error: subscriptionError } = await supabase
      .from("user_subscriptions")
      .select("subscription_status, stripe_customer_id, stripe_subscription_id, price_id, current_period_end")
      .eq("user_id", session.user.id)
      .maybeSingle();

    setState((current) => ({
      ...current,
      loading: false,
      session,
      user: session.user,
      subscription: subscription || null,
      isPro: getIsPro(subscription),
      error: subscriptionError ? subscriptionError.message : "",
    }));
  }, []);

  useEffect(() => {
    const refreshTimer = setTimeout(() => {
      refresh();
    }, 0);
    if (!supabase) {
      return () => clearTimeout(refreshTimer);
    }

    // Supabase always fires an "INITIAL_SESSION" event the instant this
    // listener attaches, reporting the same session the setTimeout above is
    // already about to fetch fresh from the server. Skip only that one
    // synthetic first event so page load doesn't fire two network requests
    // for the same subscription row. Every real change (sign in, sign out,
    // token refresh, another tab signing in) is a different event name and
    // still refreshes normally.
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "INITIAL_SESSION") return;
      refresh();
    });

    return () => {
      clearTimeout(refreshTimer);
      data.subscription.unsubscribe();
    };
  }, [refresh]);

  return {
    ...state,
    refresh,
  };
}
